import {
  ConcealedDuctLocationType,
  ConfigurationType,
  DuctRunPointType,
  GridSpaceDataType,
  LevelType
} from 'data/types';
import { getLevelIndexForSpace } from 'utils/3d';
import { getRadialDuctPassiveSpaces } from 'utils/air-handler-availability';
import { getPassiveSpaceIdsForZone } from 'utils/concealed-duct-passive-spaces';
import { resolveConcealedDuctLocation } from 'utils/concealed-duct-placement';
import {
  bendCount,
  polylineLengthPx,
  resolveRoutingRegionPx,
  routeDuctPath,
  routePathInteriorWaypointsPx,
  RoutePointType
} from 'utils/duct-route';
import { isPointInPolygon } from 'utils/footprint';
import { shouldLayOutDuctedZones } from 'utils/should-layout-ducted-zones';
import { polygonArea } from 'utils/space-polygon';
import { ZoneDuctModesMapType } from 'utils/zone-duct-modes';

export { shouldLayOutDuctedZones };

const FLEX_TAILS_PER_SUPPLY_RUN = 1;
const FLEX_TAILS_PER_RETURN_RUN = 0;
const ELBOWS_PER_SUPPLY_RUN = 2;
const ELBOWS_PER_RETURN_RUN = 2;
const VERTICAL_DROP_FT_PER_FLOOR = 10;
const ELBOWS_PER_FLOOR_JUMP = 2;
const DEFAULT_SUPPLY_TRUNK_FT = 25;
const DEFAULT_RETURN_TRUNK_FT = 12;
const MIN_SUPPLY_TRUNK_FT = 8;
const PIXELS_PER_FOOT = 10;

export interface ConcealedDuctGeometryInput {
  structure: LevelType[];
  spaces: Array<{
    uniqueId: string;
    structureSpaceId: string | null;
    registerOverridePx?: { x: number; y: number } | null;
    supplyRunPointsPx?: DuctRunPointType[] | null;
  }>;
  getGridSpacesForLevel: (levelIndex: number) => GridSpaceDataType[];
  originOverridePx?: { x: number; y: number } | null;
  returnOverridePx?: { x: number; y: number } | null;
  returnRunPointsPx?: DuctRunPointType[] | null;
  additionalSupplyRunsBySpaceId?: Map<
    string,
    Array<{
      uniqueId: string;
      x: number;
      y: number;
      points?: DuctRunPointType[];
    }>
  >;
  additionalReturnRunsPx?: Array<{
    uniqueId: string;
    x: number;
    y: number;
    points?: DuctRunPointType[];
  }>;
}

export interface RadialDuctGeometryGui {
  ductLengthFt: number;
  elbowCount: number;
  flexTailCount: number;
}

export interface ZoneRadialDuctGeometry {
  supplyDuctsBySpaceId: Map<string, RadialDuctGeometryGui>;
  supplyDuctRunsBySpaceId: Map<string, RadialDuctGeometryGui[]>;
  returnDuct: RadialDuctGeometryGui;
  returnDuctRuns: RadialDuctGeometryGui[];
}

export interface ConcealedDuctBranchLayout {
  spaceUniqueId: string;
  structureSpaceId: string | null;
  centroidPx: { x: number; y: number } | null;
  levelIndex: number | null;
  ductLengthFt: number;
  sameLevelAsOrigin: boolean;
  waypointsPx: DuctRunPointType[];
  autoWaypoints?: boolean;
  servedPassively?: boolean;
  pathPx?: RoutePointType[];
}

export interface ConcealedDuctReturnLayout {
  endpointPx: { x: number; y: number };
  levelIndex: number;
  ductLengthFt: number;
  sameLevelAsOrigin: boolean;
  containingSpaceId: string | null;
  waypointsPx: DuctRunPointType[];
  autoWaypoints?: boolean;
  pathPx?: RoutePointType[];
}

export interface ConcealedDuctAdditionalSupplyRunLayout {
  runUniqueId: string;
  spaceUniqueId: string;
  structureSpaceId: string | null;
  endpointPx: { x: number; y: number };
  levelIndex: number;
  ductLengthFt: number;
  sameLevelAsOrigin: boolean;
  waypointsPx: DuctRunPointType[];
  autoWaypoints?: boolean;
  pathPx?: RoutePointType[];
}

export interface ConcealedDuctAdditionalReturnRunLayout {
  runUniqueId: string;
  endpointPx: { x: number; y: number };
  levelIndex: number;
  ductLengthFt: number;
  sameLevelAsOrigin: boolean;
  containingSpaceId: string | null;
  waypointsPx: DuctRunPointType[];
  autoWaypoints?: boolean;
  pathPx?: RoutePointType[];
}

export interface ZoneConcealedDuctLayout {
  zoneUniqueId?: string;
  condenserUniqueId?: string;
  originPx: { x: number; y: number };
  originLevelIndex: number;
  branches: ConcealedDuctBranchLayout[];
  additionalSupplyRuns: ConcealedDuctAdditionalSupplyRunLayout[];
  returnRun: ConcealedDuctReturnLayout | null;
  additionalReturnRuns: ConcealedDuctAdditionalReturnRunLayout[];
  roomLabelsByStructureSpaceId?: Map<string, string>;
  routingRegionPx?: RoutePointType[][];
  placement?: ConcealedDuctLocationType | null;
}

interface ResolvedSpace {
  uniqueId: string;
  structureSpaceId: string;
  levelIndex: number;
  centroidPx: { x: number; y: number };
  originAnchorPx: { x: number; y: number };
  originAnchorFt: { x: number; y: number };
  areaSqFt: number;
  polygonPx: Array<{ x: number; y: number }>;
}

function computeCentroidPx(grid: GridSpaceDataType): { x: number; y: number } {
  if (grid.vertices && grid.vertices.length > 0) {
    let sx = 0;
    let sy = 0;
    grid.vertices.forEach((v) => {
      sx += v.x + grid.position.x;
      sy += v.y + grid.position.y;
    });
    return { x: sx / grid.vertices.length, y: sy / grid.vertices.length };
  }
  return {
    x: grid.position.x + grid.position.width / 2,
    y: grid.position.y + grid.position.height / 2
  };
}

function pxToFt(p: { x: number; y: number }): { x: number; y: number } {
  return { x: p.x / PIXELS_PER_FOOT, y: p.y / PIXELS_PER_FOOT };
}

export function ductRunPolylineLengthPx(
  originPx: { x: number; y: number },
  waypointsPx: Array<{ x: number; y: number }> | null | undefined,
  endpointPx: { x: number; y: number }
): number {
  const points = [originPx, ...(waypointsPx ?? []), endpointPx];
  let total = 0;
  for (let i = 0; i < points.length - 1; i += 1) {
    total += Math.hypot(
      points[i + 1].x - points[i].x,
      points[i + 1].y - points[i].y
    );
  }
  return total;
}

function defaultSupplyRun(): RadialDuctGeometryGui {
  return {
    ductLengthFt: DEFAULT_SUPPLY_TRUNK_FT,
    elbowCount: ELBOWS_PER_SUPPLY_RUN,
    flexTailCount: FLEX_TAILS_PER_SUPPLY_RUN
  };
}

function defaultReturnRun(): RadialDuctGeometryGui {
  return {
    ductLengthFt: DEFAULT_RETURN_TRUNK_FT,
    elbowCount: ELBOWS_PER_RETURN_RUN,
    flexTailCount: FLEX_TAILS_PER_RETURN_RUN
  };
}

function getSpacePolygonPx(grid: GridSpaceDataType): Array<{
  x: number;
  y: number;
}> {
  if (grid.vertices && grid.vertices.length >= 3) {
    return grid.vertices.map((v) => ({
      x: v.x + grid.position.x,
      y: v.y + grid.position.y
    }));
  }
  const { x, y, width, height } = grid.position;
  return [
    { x, y },
    { x: x + width, y },
    { x: x + width, y: y + height },
    { x, y: y + height }
  ];
}

function resolveSpace(
  space: {
    uniqueId: string;
    structureSpaceId: string | null;
    registerOverridePx?: { x: number; y: number } | null;
  },
  structure: LevelType[],
  getGrid: (levelIndex: number) => GridSpaceDataType[]
): ResolvedSpace | null {
  if (!space.structureSpaceId) return null;
  const levelIndex = getLevelIndexForSpace(space.structureSpaceId, structure);
  if (levelIndex < 0) return null;
  const grid = getGrid(levelIndex).find(
    (g) => g.spaceId === space.structureSpaceId
  );
  if (!grid) return null;
  const baseCentroidPx = computeCentroidPx(grid);
  const centroidPx = space.registerOverridePx ?? baseCentroidPx;
  const polygonAreaPx =
    grid.vertices && grid.vertices.length >= 3
      ? polygonArea(grid.vertices)
      : grid.position.width * grid.position.height;
  return {
    uniqueId: space.uniqueId,
    structureSpaceId: space.structureSpaceId,
    levelIndex,
    centroidPx,
    originAnchorPx: baseCentroidPx,
    originAnchorFt: pxToFt(baseCentroidPx),
    areaSqFt: polygonAreaPx / (PIXELS_PER_FOOT * PIXELS_PER_FOOT),
    polygonPx: getSpacePolygonPx(grid)
  };
}

function computeAirHandlerOrigin(usable: ResolvedSpace[]): {
  originFt: { x: number; y: number };
  originLevelIndex: number;
} {
  const accumByLevel = new Map<
    number,
    { x: number; y: number; weight: number }
  >();
  for (const r of usable) {
    const cur = accumByLevel.get(r.levelIndex) ?? { x: 0, y: 0, weight: 0 };
    cur.x += r.originAnchorFt.x * r.areaSqFt;
    cur.y += r.originAnchorFt.y * r.areaSqFt;
    cur.weight += r.areaSqFt;
    accumByLevel.set(r.levelIndex, cur);
  }
  let originLevelIndex = usable[0].levelIndex;
  let bestWeight = 0;
  accumByLevel.forEach((v, lvl) => {
    if (v.weight > bestWeight) {
      bestWeight = v.weight;
      originLevelIndex = lvl;
    }
  });
  const accum = accumByLevel.get(originLevelIndex) ?? { x: 0, y: 0, weight: 1 };
  return {
    originFt: {
      x: accum.x / Math.max(accum.weight, 1),
      y: accum.y / Math.max(accum.weight, 1)
    },
    originLevelIndex
  };
}

function pickLevelClosestToPx(
  pointPx: { x: number; y: number },
  usable: ResolvedSpace[]
): number {
  let bestLevel = usable[0].levelIndex;
  let bestDist = Number.POSITIVE_INFINITY;
  for (const r of usable) {
    const d = Math.hypot(
      r.originAnchorPx.x - pointPx.x,
      r.originAnchorPx.y - pointPx.y
    );
    if (d < bestDist) {
      bestDist = d;
      bestLevel = r.levelIndex;
    }
  }
  return bestLevel;
}

function resolveOrigin(
  usable: ResolvedSpace[],
  originOverridePx?: { x: number; y: number } | null
): { originFt: { x: number; y: number }; originLevelIndex: number } {
  if (originOverridePx) {
    return {
      originFt: pxToFt(originOverridePx),
      originLevelIndex: pickLevelClosestToPx(originOverridePx, usable)
    };
  }
  return computeAirHandlerOrigin(usable);
}

function interiorSamplesPx(
  room: ResolvedSpace
): Array<{ x: number; y: number }> {
  const xs = room.polygonPx.map((p) => p.x);
  const ys = room.polygonPx.map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const GRID_STEPS = 5;
  const samples: Array<{ x: number; y: number }> = [];
  for (let i = 1; i < GRID_STEPS; i += 1) {
    for (let j = 1; j < GRID_STEPS; j += 1) {
      const candidate = {
        x: minX + ((maxX - minX) * i) / GRID_STEPS,
        y: minY + ((maxY - minY) * j) / GRID_STEPS
      };
      if (isPointInPolygon(candidate, room.polygonPx)) {
        samples.push(candidate);
      }
    }
  }
  if (samples.length === 0) samples.push(room.centroidPx);
  return samples;
}

function pickDefaultReturnEndpointPx(
  originPx: { x: number; y: number },
  largestRoom: ResolvedSpace,
  targetDistancePx: number,
  supplyEndpointsPx: Array<{ x: number; y: number }>
): { x: number; y: number } {
  const SAMPLE_COUNT = 36;
  const candidates: Array<{ x: number; y: number }> = [];
  for (let i = 0; i < SAMPLE_COUNT; i += 1) {
    const angle = (i / SAMPLE_COUNT) * Math.PI * 2;
    const candidate = {
      x: originPx.x + Math.cos(angle) * targetDistancePx,
      y: originPx.y + Math.sin(angle) * targetDistancePx
    };
    if (isPointInPolygon(candidate, largestRoom.polygonPx)) {
      candidates.push(candidate);
    }
  }

  const pool =
    candidates.length > 0 ? candidates : interiorSamplesPx(largestRoom);

  const scoreByDistanceFromSupplies = (p: { x: number; y: number }): number => {
    if (supplyEndpointsPx.length === 0) return Number.POSITIVE_INFINITY;
    let minDist = Number.POSITIVE_INFINITY;
    for (const s of supplyEndpointsPx) {
      const d = Math.hypot(p.x - s.x, p.y - s.y);
      if (d < minDist) minDist = d;
    }
    return minDist;
  };

  let best = pool[0];
  let bestScore = scoreByDistanceFromSupplies(best);
  for (let i = 1; i < pool.length; i += 1) {
    const c = pool[i];
    const score = scoreByDistanceFromSupplies(c);
    if (score > bestScore) {
      best = c;
      bestScore = score;
    }
  }
  return best;
}

type RoutedRunMetrics = {
  lengthFt: number;
  bends: number;
  pathPx: RoutePointType[];
  waypointsPx: DuctRunPointType[];
  autoWaypoints: boolean;
};

function routeRunMetrics(
  regionPx: RoutePointType[][],
  originPx: { x: number; y: number },
  endpointPx: { x: number; y: number },
  originLevelIndex: number,
  endpointLevelIndex: number,
  waypointsPx?: DuctRunPointType[] | null
): RoutedRunMetrics {
  const floorDelta = Math.abs(endpointLevelIndex - originLevelIndex);
  if (waypointsPx && waypointsPx.length > 0) {
    const pathPx = [
      originPx,
      ...waypointsPx.map((p) => ({ x: p.x, y: p.y })),
      endpointPx
    ];
    const horizontalFt =
      ductRunPolylineLengthPx(originPx, waypointsPx, endpointPx) /
      PIXELS_PER_FOOT;
    const verticalFt = floorDelta * VERTICAL_DROP_FT_PER_FLOOR;
    return {
      lengthFt: Math.round((horizontalFt + verticalFt) * 10) / 10,
      bends: bendCount(pathPx),
      pathPx,
      waypointsPx,
      autoWaypoints: false
    };
  }
  if (floorDelta === 0) {
    const pathPx = routeDuctPath(regionPx, originPx, endpointPx);
    const horizontalFt = polylineLengthPx(pathPx) / PIXELS_PER_FOOT;
    return {
      lengthFt: Math.round(horizontalFt * 10) / 10,
      bends: bendCount(pathPx),
      pathPx,
      waypointsPx: routePathInteriorWaypointsPx(pathPx),
      autoWaypoints: true
    };
  }
  const horizontalFt =
    Math.hypot(endpointPx.x - originPx.x, endpointPx.y - originPx.y) /
    PIXELS_PER_FOOT;
  const verticalFt = floorDelta * VERTICAL_DROP_FT_PER_FLOOR;
  return {
    lengthFt: Math.round((horizontalFt + verticalFt) * 10) / 10,
    bends: 0,
    pathPx: [originPx, endpointPx],
    waypointsPx: [],
    autoWaypoints: false
  };
}

function pickRoomContainingPoint(
  pointPx: { x: number; y: number },
  usable: ResolvedSpace[],
  largestRoom: ResolvedSpace
): ResolvedSpace {
  for (const r of usable) {
    if (isPointInPolygon(pointPx, r.polygonPx)) return r;
  }
  return largestRoom;
}

function pickLargestRoom(usable: ResolvedSpace[]): ResolvedSpace {
  let best = usable[0];
  for (let i = 1; i < usable.length; i += 1) {
    if (usable[i].areaSqFt > best.areaSqFt) best = usable[i];
  }
  return best;
}

function computeSupplyRun(
  regionPx: RoutePointType[][],
  originPx: { x: number; y: number },
  originLevelIndex: number,
  space: ResolvedSpace,
  waypointsPx?: DuctRunPointType[] | null
): { run: RadialDuctGeometryGui; routed: RoutedRunMetrics } {
  const floorDelta = Math.abs(space.levelIndex - originLevelIndex);
  const routed = routeRunMetrics(
    regionPx,
    originPx,
    space.centroidPx,
    originLevelIndex,
    space.levelIndex,
    waypointsPx
  );
  return {
    run: {
      ductLengthFt: Math.max(MIN_SUPPLY_TRUNK_FT, Math.round(routed.lengthFt)),
      elbowCount:
        ELBOWS_PER_SUPPLY_RUN +
        floorDelta * ELBOWS_PER_FLOOR_JUMP +
        routed.bends,
      flexTailCount: FLEX_TAILS_PER_SUPPLY_RUN
    },
    routed
  };
}

export function buildConcealedDuctGeometry(
  input: ConcealedDuctGeometryInput
): ZoneRadialDuctGeometry {
  const {
    structure,
    spaces,
    getGridSpacesForLevel: getGrid,
    originOverridePx,
    returnOverridePx,
    returnRunPointsPx,
    additionalSupplyRunsBySpaceId,
    additionalReturnRunsPx
  } = input;
  const supplyDuctsBySpaceId = new Map<string, RadialDuctGeometryGui>();

  if (spaces.length === 0) {
    const fallbackReturn = defaultReturnRun();
    return {
      supplyDuctsBySpaceId,
      supplyDuctRunsBySpaceId: new Map(),
      returnDuct: fallbackReturn,
      returnDuctRuns: [fallbackReturn]
    };
  }

  const resolved = spaces.map((space) =>
    resolveSpace(space, structure, getGrid)
  );
  const usable = resolved.filter((r): r is NonNullable<typeof r> => r !== null);
  if (usable.length === 0) {
    const emptyRunsBySpace = new Map<string, RadialDuctGeometryGui[]>();
    spaces.forEach((s) => {
      const fallback = defaultSupplyRun();
      supplyDuctsBySpaceId.set(s.uniqueId, fallback);
      emptyRunsBySpace.set(s.uniqueId, [fallback]);
    });
    const fallbackReturn = defaultReturnRun();
    return {
      supplyDuctsBySpaceId,
      supplyDuctRunsBySpaceId: emptyRunsBySpace,
      returnDuct: fallbackReturn,
      returnDuctRuns: [fallbackReturn]
    };
  }

  const { originFt, originLevelIndex } = resolveOrigin(
    usable,
    originOverridePx
  );

  const originPx = {
    x: originFt.x * PIXELS_PER_FOOT,
    y: originFt.y * PIXELS_PER_FOOT
  };
  const regionPx = resolveRoutingRegionPx(structure, originLevelIndex, getGrid);

  const supplyDuctRunsBySpaceId = new Map<string, RadialDuctGeometryGui[]>();

  let longestSupplyFt = 0;
  for (const space of spaces) {
    const r = resolved.find((x) => x?.uniqueId === space.uniqueId) ?? null;
    if (r) {
      const { run: primary } = computeSupplyRun(
        regionPx,
        originPx,
        originLevelIndex,
        r,
        space.supplyRunPointsPx
      );
      const runs: RadialDuctGeometryGui[] = [primary];
      const extras = additionalSupplyRunsBySpaceId?.get(space.uniqueId) ?? [];
      const floorDelta = Math.abs(r.levelIndex - originLevelIndex);
      let representative = primary;
      extras.forEach((extra) => {
        const routed = routeRunMetrics(
          regionPx,
          originPx,
          { x: extra.x, y: extra.y },
          originLevelIndex,
          r.levelIndex,
          extra.points
        );
        const additional: RadialDuctGeometryGui = {
          ductLengthFt: routed.lengthFt,
          elbowCount:
            ELBOWS_PER_SUPPLY_RUN +
            floorDelta * ELBOWS_PER_FLOOR_JUMP +
            routed.bends,
          flexTailCount: primary.flexTailCount
        };
        runs.push(additional);
        if (routed.lengthFt > representative.ductLengthFt) {
          representative = additional;
        }
      });
      longestSupplyFt = Math.max(longestSupplyFt, representative.ductLengthFt);
      supplyDuctsBySpaceId.set(space.uniqueId, representative);
      supplyDuctRunsBySpaceId.set(space.uniqueId, runs);
    } else {
      const fallback = defaultSupplyRun();
      supplyDuctsBySpaceId.set(space.uniqueId, fallback);
      supplyDuctRunsBySpaceId.set(space.uniqueId, [fallback]);
    }
  }

  const primaryReturn: RadialDuctGeometryGui = returnOverridePx
    ? (() => {
        const routed = routeRunMetrics(
          regionPx,
          originPx,
          returnOverridePx,
          originLevelIndex,
          pickRoomContainingPoint(
            returnOverridePx,
            usable,
            pickLargestRoom(usable)
          ).levelIndex,
          returnRunPointsPx
        );
        return {
          ductLengthFt: routed.lengthFt,
          elbowCount: ELBOWS_PER_RETURN_RUN + routed.bends,
          flexTailCount: FLEX_TAILS_PER_RETURN_RUN
        };
      })()
    : {
        ductLengthFt: Math.max(
          DEFAULT_RETURN_TRUNK_FT,
          Math.round(longestSupplyFt * 0.5)
        ),
        elbowCount: ELBOWS_PER_RETURN_RUN,
        flexTailCount: FLEX_TAILS_PER_RETURN_RUN
      };

  const returnDuctRuns: RadialDuctGeometryGui[] = [primaryReturn];

  let representativeReturn = primaryReturn;
  (additionalReturnRunsPx ?? []).forEach((extra) => {
    const endpointLevel = pickRoomContainingPoint(
      { x: extra.x, y: extra.y },
      usable,
      pickLargestRoom(usable)
    ).levelIndex;
    const routed = routeRunMetrics(
      regionPx,
      originPx,
      { x: extra.x, y: extra.y },
      originLevelIndex,
      endpointLevel,
      extra.points
    );
    const additional: RadialDuctGeometryGui = {
      ductLengthFt: routed.lengthFt,
      elbowCount: ELBOWS_PER_RETURN_RUN + routed.bends,
      flexTailCount: FLEX_TAILS_PER_RETURN_RUN
    };
    returnDuctRuns.push(additional);
    if (routed.lengthFt > representativeReturn.ductLengthFt) {
      representativeReturn = additional;
    }
  });

  return {
    supplyDuctsBySpaceId,
    supplyDuctRunsBySpaceId,
    returnDuct: { ...representativeReturn },
    returnDuctRuns
  };
}

export function buildConcealedDuctLayout(
  input: ConcealedDuctGeometryInput
): ZoneConcealedDuctLayout | null {
  const {
    structure,
    spaces,
    getGridSpacesForLevel: getGrid,
    originOverridePx,
    returnOverridePx,
    returnRunPointsPx,
    additionalSupplyRunsBySpaceId,
    additionalReturnRunsPx
  } = input;
  if (spaces.length === 0) return null;

  const resolved = spaces.map((space) =>
    resolveSpace(space, structure, getGrid)
  );
  const usable = resolved.filter((r): r is NonNullable<typeof r> => r !== null);
  if (usable.length === 0) return null;

  const { originFt, originLevelIndex } = resolveOrigin(
    usable,
    originOverridePx
  );
  const originPx = {
    x: originFt.x * PIXELS_PER_FOOT,
    y: originFt.y * PIXELS_PER_FOOT
  };
  const regionPx = resolveRoutingRegionPx(structure, originLevelIndex, getGrid);

  let longestSupplyFt = 0;
  const branches: ConcealedDuctBranchLayout[] = spaces.map((space, i) => {
    const r = resolved[i];
    if (!r) {
      return {
        spaceUniqueId: space.uniqueId,
        structureSpaceId: space.structureSpaceId,
        centroidPx: null,
        levelIndex: null,
        ductLengthFt: DEFAULT_SUPPLY_TRUNK_FT,
        sameLevelAsOrigin: false,
        waypointsPx: []
      };
    }
    const supply = computeSupplyRun(
      regionPx,
      originPx,
      originLevelIndex,
      r,
      space.supplyRunPointsPx
    );
    longestSupplyFt = Math.max(longestSupplyFt, supply.run.ductLengthFt);
    return {
      spaceUniqueId: space.uniqueId,
      structureSpaceId: space.structureSpaceId,
      centroidPx: r.centroidPx,
      levelIndex: r.levelIndex,
      ductLengthFt: supply.run.ductLengthFt,
      sameLevelAsOrigin: r.levelIndex === originLevelIndex,
      waypointsPx: supply.routed.waypointsPx,
      autoWaypoints: supply.routed.autoWaypoints,
      pathPx: supply.routed.pathPx
    };
  });

  const largestRoom = pickLargestRoom(usable);
  const calculatedReturnFt = Math.max(
    DEFAULT_RETURN_TRUNK_FT,
    Math.round(longestSupplyFt * 0.5)
  );
  let returnRun: ConcealedDuctReturnLayout | null = null;
  if (returnOverridePx) {
    const room = pickRoomContainingPoint(returnOverridePx, usable, largestRoom);
    const routed = routeRunMetrics(
      regionPx,
      originPx,
      returnOverridePx,
      originLevelIndex,
      room.levelIndex,
      returnRunPointsPx
    );
    returnRun = {
      endpointPx: returnOverridePx,
      levelIndex: room.levelIndex,
      ductLengthFt: routed.lengthFt,
      sameLevelAsOrigin: room.levelIndex === originLevelIndex,
      containingSpaceId: room.structureSpaceId,
      waypointsPx: routed.waypointsPx,
      autoWaypoints: routed.autoWaypoints,
      pathPx: routed.pathPx
    };
  } else {
    const supplyEndpoints = branches
      .filter((b) => b.centroidPx !== null && b.sameLevelAsOrigin)
      .map((b) => b.centroidPx as { x: number; y: number });
    const targetDistancePx = calculatedReturnFt * PIXELS_PER_FOOT;
    const endpointPx = pickDefaultReturnEndpointPx(
      originPx,
      largestRoom,
      targetDistancePx,
      supplyEndpoints
    );
    const routed = routeRunMetrics(
      regionPx,
      originPx,
      endpointPx,
      originLevelIndex,
      largestRoom.levelIndex,
      returnRunPointsPx
    );
    returnRun = {
      endpointPx,
      levelIndex: largestRoom.levelIndex,
      ductLengthFt: routed.lengthFt,
      sameLevelAsOrigin: largestRoom.levelIndex === originLevelIndex,
      containingSpaceId: largestRoom.structureSpaceId,
      waypointsPx: routed.waypointsPx,
      autoWaypoints: routed.autoWaypoints,
      pathPx: routed.pathPx
    };
  }

  const additionalSupplyRuns: ConcealedDuctAdditionalSupplyRunLayout[] = [];
  spaces.forEach((space, i) => {
    const r = resolved[i];
    if (!r) return;
    const extras = additionalSupplyRunsBySpaceId?.get(space.uniqueId) ?? [];
    extras.forEach((extra) => {
      const endpointPx = { x: extra.x, y: extra.y };
      const routed = routeRunMetrics(
        regionPx,
        originPx,
        endpointPx,
        originLevelIndex,
        r.levelIndex,
        extra.points
      );
      additionalSupplyRuns.push({
        runUniqueId: extra.uniqueId,
        spaceUniqueId: space.uniqueId,
        structureSpaceId: space.structureSpaceId,
        endpointPx,
        levelIndex: r.levelIndex,
        ductLengthFt: routed.lengthFt,
        sameLevelAsOrigin: r.levelIndex === originLevelIndex,
        waypointsPx: routed.waypointsPx,
        autoWaypoints: routed.autoWaypoints,
        pathPx: routed.pathPx
      });
    });
  });

  const additionalReturnRuns: ConcealedDuctAdditionalReturnRunLayout[] = [];
  (additionalReturnRunsPx ?? []).forEach((extra) => {
    const endpointPx = { x: extra.x, y: extra.y };
    const room = pickRoomContainingPoint(endpointPx, usable, largestRoom);
    const routed = routeRunMetrics(
      regionPx,
      originPx,
      endpointPx,
      originLevelIndex,
      room.levelIndex,
      extra.points
    );
    additionalReturnRuns.push({
      runUniqueId: extra.uniqueId,
      endpointPx,
      levelIndex: room.levelIndex,
      ductLengthFt: routed.lengthFt,
      sameLevelAsOrigin: room.levelIndex === originLevelIndex,
      containingSpaceId: room.structureSpaceId,
      waypointsPx: routed.waypointsPx,
      autoWaypoints: routed.autoWaypoints,
      pathPx: routed.pathPx
    });
  });

  return {
    originPx,
    originLevelIndex,
    branches,
    additionalSupplyRuns,
    returnRun,
    routingRegionPx: regionPx,
    additionalReturnRuns
  };
}

type ConfigurationZoneType =
  ConfigurationType['condensers'][number]['zones'][number];

function isConcealedDuctZone(zone: ConfigurationZoneType): boolean {
  const ah = zone.airHandler;
  if (!ah) return false;
  if (
    ah.airHandlerTypeCode !== 'concealed' &&
    ah.airHandlerTypeCode !== 'concealed_high_static'
  ) {
    return false;
  }
  return zone.zoneItems.length > 0;
}

function isDuctedLayoutZone(zone: ConfigurationZoneType): boolean {
  return (
    zone.type === 'ducted' && !!zone.airHandler && zone.zoneItems.length > 0
  );
}

function isDuctLayoutZone(
  zone: ConfigurationZoneType,
  configuration: ConfigurationType
): boolean {
  return (
    isConcealedDuctZone(zone) ||
    (shouldLayOutDuctedZones(configuration) && isDuctedLayoutZone(zone))
  );
}

export function supplyRunLengthFtForPoints(
  originPx: { x: number; y: number },
  endpointPx: { x: number; y: number }
): number {
  const horizontalFt =
    Math.hypot(endpointPx.x - originPx.x, endpointPx.y - originPx.y) /
    PIXELS_PER_FOOT;
  return Math.max(MIN_SUPPLY_TRUNK_FT, Math.round(horizontalFt));
}

export function sameLevelRunLengthFtForPoints(
  originPx: { x: number; y: number },
  endpointPx: { x: number; y: number }
): number {
  const horizontalFt =
    Math.hypot(endpointPx.x - originPx.x, endpointPx.y - originPx.y) /
    PIXELS_PER_FOOT;
  return Math.round(horizontalFt * 10) / 10;
}

export type RoutedRunPreviewType = {
  lengthFt: number;
  pathPx: RoutePointType[];
};

export function routedSupplyRunPreview(
  regionPx: RoutePointType[][],
  originPx: { x: number; y: number },
  endpointPx: { x: number; y: number }
): RoutedRunPreviewType {
  const pathPx = routeDuctPath(regionPx, originPx, endpointPx);
  const horizontalFt = polylineLengthPx(pathPx) / PIXELS_PER_FOOT;
  return {
    lengthFt: Math.max(MIN_SUPPLY_TRUNK_FT, Math.round(horizontalFt)),
    pathPx
  };
}

export function routedSameLevelRunPreview(
  regionPx: RoutePointType[][],
  originPx: { x: number; y: number },
  endpointPx: { x: number; y: number }
): RoutedRunPreviewType {
  const pathPx = routeDuctPath(regionPx, originPx, endpointPx);
  const horizontalFt = polylineLengthPx(pathPx) / PIXELS_PER_FOOT;
  return {
    lengthFt: Math.round(horizontalFt * 10) / 10,
    pathPx
  };
}

export function getConcealedDuctOriginOverridePx(
  zone: ConfigurationType['condensers'][number]['zones'][number]
): { x: number; y: number } | null {
  const ah = zone.airHandler;
  if (!ah?.userPositioned) return null;
  const halfSquares = 1.5;
  return {
    x: (ah.positionX + halfSquares) * PIXELS_PER_FOOT,
    y: (ah.positionY + halfSquares) * PIXELS_PER_FOOT
  };
}

export function getDefaultReturnEndpointPxForZone(
  zone: ConfigurationType['condensers'][number]['zones'][number],
  structure: LevelType[],
  getGridSpacesForLevel: (levelIndex: number) => GridSpaceDataType[]
): { x: number; y: number } | null {
  const additionalSupplyRunsBySpaceId = new Map<
    string,
    Array<{ uniqueId: string; x: number; y: number }>
  >();
  zone.zoneItems.forEach((item) => {
    if (item.additionalSupplyRuns && item.additionalSupplyRuns.length > 0) {
      additionalSupplyRunsBySpaceId.set(
        item.structureItemId,
        item.additionalSupplyRuns
      );
    }
  });
  const layout = buildConcealedDuctLayout({
    structure,
    spaces: zone.zoneItems.map((item) => ({
      uniqueId: item.structureItemId,
      structureSpaceId: item.structureItemId,
      registerOverridePx: item.registerOverride ?? null,
      supplyRunPointsPx: item.supplyRunPoints ?? null
    })),
    getGridSpacesForLevel,
    originOverridePx: getConcealedDuctOriginOverridePx(zone),
    returnOverridePx: null,
    additionalSupplyRunsBySpaceId,
    additionalReturnRunsPx: zone.additionalReturnRuns ?? []
  });
  if (!layout?.returnRun?.sameLevelAsOrigin) return null;
  return layout.returnRun.endpointPx;
}

export function buildConcealedDuctLayoutsForConfiguration(
  configuration: ConfigurationType | undefined,
  structure: LevelType[],
  getGridSpacesForLevel: (levelIndex: number) => GridSpaceDataType[],
  zoneDuctModes?: ZoneDuctModesMapType
): Map<string, ZoneConcealedDuctLayout> {
  const out = new Map<string, ZoneConcealedDuctLayout>();
  if (!configuration) return out;

  const concealedZoneEntries = configuration.condensers.flatMap((condenser) =>
    condenser.zones
      .filter((zone) => isDuctLayoutZone(zone, configuration))
      .map((zone) => ({ condenser, zone }))
  );

  concealedZoneEntries.forEach(({ condenser, zone }) => {
    const additionalSupplyRunsBySpaceId = new Map<
      string,
      Array<{
        uniqueId: string;
        x: number;
        y: number;
        points?: DuctRunPointType[];
      }>
    >();
    zone.zoneItems.forEach((item) => {
      if (item.additionalSupplyRuns && item.additionalSupplyRuns.length > 0) {
        additionalSupplyRunsBySpaceId.set(
          item.structureItemId,
          item.additionalSupplyRuns
        );
      }
    });

    const layout = buildConcealedDuctLayout({
      structure,
      spaces: zone.zoneItems.map((item) => ({
        uniqueId: item.structureItemId,
        structureSpaceId: item.structureItemId,
        registerOverridePx: item.registerOverride ?? null,
        supplyRunPointsPx: item.supplyRunPoints ?? null
      })),
      getGridSpacesForLevel,
      originOverridePx: getConcealedDuctOriginOverridePx(zone),
      returnOverridePx: zone.returnOverride ?? null,
      returnRunPointsPx: zone.returnRunPoints ?? null,
      additionalSupplyRunsBySpaceId,
      additionalReturnRunsPx: zone.additionalReturnRuns ?? []
    });
    if (layout) {
      const roomLabelsByStructureSpaceId = new Map<string, string>();
      const originLevel = structure[layout.originLevelIndex];
      const sameLevelSpaces = originLevel
        ? getGridSpacesForLevel(layout.originLevelIndex)
        : [];
      zone.zoneItems.forEach((item) => {
        const gs = sameLevelSpaces.find(
          (s) => s.spaceId === item.structureItemId
        );
        if (gs?.label) {
          roomLabelsByStructureSpaceId.set(item.structureItemId, gs.label);
        }
      });

      const passiveSpaceIds = getPassiveSpaceIdsForZone(
        zone,
        condenser.validSystems ?? [],
        zoneDuctModes?.[zone.uniqueId],
        getRadialDuctPassiveSpaces(condenser.airHandlerOptions)
      );

      out.set(zone.uniqueId, {
        ...layout,
        branches: layout.branches.map((branch) => ({
          ...branch,
          servedPassively: passiveSpaceIds.has(branch.spaceUniqueId)
        })),
        zoneUniqueId: zone.uniqueId,
        condenserUniqueId: condenser.uniqueId,
        roomLabelsByStructureSpaceId,
        placement: resolveConcealedDuctLocation(
          zone.airHandler?.concealedLocation,
          layout.originLevelIndex,
          structure
        )
      });
    }
  });

  return out;
}
